// frontend/src/store/walletStore.js

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import walletAPI from '../services/wallet.api.js';
import { toast } from 'react-hot-toast';

// ✅ Prevent duplicate funding calls
let isFundingInProgress = false;

/**
 * NOTE ON THE `toast` IMPORT:
 * Most toasts have been removed from this store so that pages/components
 * decide what to show. The ONLY remaining toast calls live inside
 * `fundWallet`, where they drive the payment overlay's lifecycle
 * (loading spinner + dismiss on success/cancel). Those aren't user
 * notifications — they're UI state for the injected Monnify iframe.
 * If you want to remove those too, move the loading indicator into the
 * overlay DOM element itself and delete the import entirely.
 */

const useWalletStore = create(
  persist(
    (set, get) => ({
      wallet: null,
      transactions: [],
      withdrawalHistory: [],
      transactionSummary: null,
      banks: [],
      virtualAccount: null,
      virtualAccountLoading: false,
      pendingTransfers: [],
      isLoading: false,
      error: null,
      paymentModalOpen: false,
      user: null,

      // ============================================
      // EXISTING METHODS
      // ============================================

      getWallet: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.getWallet();
          set({ wallet: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getTransactions: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.getTransactions(params);
          set({ transactions: response.data.transactions });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      /**
       * fundWallet — payment modal lifecycle.
       *
       * The remaining toast.* calls here are NOT user notifications; they
       * render the "Processing payment..." indicator while polling for
       * Monnify confirmation, dismiss it when the modal closes, and show
       * a final success/info/error tied to the modal. Removing them
       * without a replacement loading indicator would leave the iframe
       * with no visible status.
       */
      fundWallet: async (amount) => {
        if (isFundingInProgress) {
          console.warn('⚠️ Funding already in progress, ignoring duplicate call');
          return;
        }

        isFundingInProgress = true;
        set({ isLoading: true, error: null });

        try {
          const response = await walletAPI.fundWallet(amount);

          if (response.data.authorizationUrl) {
            const paymentUrl = response.data.authorizationUrl;
            const reference = response.data.reference;

            const existingOverlay = document.getElementById('monnify-payment-overlay');
            if (existingOverlay) {
              document.body.removeChild(existingOverlay);
            }

            const overlay = document.createElement('div');
            overlay.id = 'monnify-payment-overlay';
            overlay.style.cssText = `
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background: rgba(0, 0, 0, 0.5);
              backdrop-filter: blur(4px);
              z-index: 99998;
              display: flex;
              align-items: center;
              justify-content: center;
              animation: fadeIn 0.2s ease-out;
            `;

            const modal = document.createElement('div');
            modal.style.cssText = `
              position: relative;
              width: 90%;
              max-width: 480px;
              height: 90%;
              max-height: 700px;
              background: #FFFFFF;
              border-radius: 16px;
              overflow: hidden;
              box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
              animation: slideUp 0.3s ease-out;
            `;

            const iframe = document.createElement('iframe');
            iframe.id = 'monnify-payment-iframe';
            iframe.src = paymentUrl;
            iframe.style.cssText = `
              width: 100%;
              height: 100%;
              border: none;
              background: #FFFFFF;
            `;
            iframe.allow = 'payment';

            const closeBtn = document.createElement('button');
            closeBtn.id = 'monnify-payment-close';
            closeBtn.innerHTML = '✕';
            closeBtn.style.cssText = `
              position: absolute;
              top: 12px;
              right: 12px;
              z-index: 100000;
              background: rgba(255, 255, 255, 0.95);
              border: 1px solid #E5E7EB;
              font-size: 18px;
              cursor: pointer;
              padding: 0;
              border-radius: 50%;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              color: #374151;
              width: 36px;
              height: 36px;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.2s;
              font-weight: 400;
            `;

            closeBtn.addEventListener('mouseenter', () => {
              closeBtn.style.background = '#F3F4F6';
              closeBtn.style.transform = 'scale(1.05)';
            });
            closeBtn.addEventListener('mouseleave', () => {
              closeBtn.style.background = 'rgba(255, 255, 255, 0.95)';
              closeBtn.style.transform = 'scale(1)';
            });

            let isClosed = false;
            let toastId = null;

            const closeIframe = () => {
              if (isClosed) return;
              isClosed = true;
              if (document.body.contains(overlay)) {
                document.body.removeChild(overlay);
              }
              if (toastId) {
                toast.dismiss(toastId);
              }
              isFundingInProgress = false;
              toast.info('Payment was canceled');
            };

            closeBtn.onclick = closeIframe;

            overlay.onclick = (e) => {
              if (e.target === overlay) {
                closeIframe();
              }
            };

            const handleEscape = (e) => {
              if (e.key === 'Escape') {
                closeIframe();
                document.removeEventListener('keydown', handleEscape);
              }
            };
            document.addEventListener('keydown', handleEscape);

            modal.appendChild(iframe);
            modal.appendChild(closeBtn);
            overlay.appendChild(modal);
            document.body.appendChild(overlay);

            toastId = toast.loading('Processing payment...');

            let attempts = 0;
            const checkInterval = setInterval(async () => {
              attempts++;
              try {
                const status = await walletAPI.verifyFunding(reference);
                if (status.data?.success || status.data?.alreadyProcessed) {
                  clearInterval(checkInterval);
                  if (toastId) toast.dismiss(toastId);
                  toast.success('Wallet funded successfully!');
                  await get().getWallet();
                  await get().getTransactions();
                  isFundingInProgress = false;
                  if (document.body.contains(overlay)) {
                    document.body.removeChild(overlay);
                  }
                  document.removeEventListener('keydown', handleEscape);
                } else if (attempts > 30) {
                  clearInterval(checkInterval);
                  if (toastId) toast.dismiss(toastId);
                  toast.info('Complete your payment and check your wallet.');
                  isFundingInProgress = false;
                }
              } catch (error) {
                console.log('Polling error:', error);
              }
            }, 5000);

            const style = document.createElement('style');
            style.textContent = `
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes slideUp {
                from { transform: translateY(20px); opacity: 0; }
                to { transform: translateY(0); opacity: 1; }
              }
            `;
            document.head.appendChild(style);

            return response.data;
          }

          throw new Error('Failed to initialize payment');
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('❌ fundWallet Error:', message);
          set({ error: message });
          isFundingInProgress = false;
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      verifyFunding: async (reference) => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.verifyFunding(reference);

          if (response.success) {
            if (!response.data.alreadyProcessed) {
              await get().getWallet();
            } else {
              await get().getWallet();
            }
          }

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getTransactionSummary: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.getTransactionSummary();
          set({ transactionSummary: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      /**
       * Legacy virtual account fetch (older single-bank-preference flow).
       * Backend returns { success, account } — normalized to just the
       * flat account object (or null) so every consumer of `virtualAccount`
       * in the store can rely on the same shape.
       */
      getVirtualAccount: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.getVirtualAccount();
          set({ virtualAccount: response.data?.account || null });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      generateBankTransfer: async (amount) => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.generateBankTransfer(amount);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      verifyBankTransfer: async (reference) => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.verifyBankTransfer(reference);

          if (response.success && response.data?.confirmed) {
            await get().getWallet();
          }

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getPendingTransfers: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.getPendingTransfers();
          set({ pendingTransfers: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // ============================================
      // V2 VIRTUAL ACCOUNT METHODS
      // ============================================

      /**
       * Create a virtual account with BVN/NIN verification.
       * Backend returns { account, isNew } — normalized to the flat
       * account object so it matches every other virtualAccount setter.
       */
      createVirtualAccount: async (bvn, nin) => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.createVirtualAccount(bvn, nin);

          if (response.success) {
            set({ virtualAccount: response.data?.account || null });
          }

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          const errorCode = error.response?.data?.error?.code;

          // Handle ACCOUNT_EXISTS by fetching existing account
          if (errorCode === 'ACCOUNT_EXISTS') {
            await get().getVirtualAccountDetails();
          }

          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      /**
       * Get virtual account details.
       * Backend returns { success, exists, account } — normalized to
       * the flat account object (or null when none exists) so
       * `virtualAccount?.accountNumber` etc. always works the same
       * way regardless of which method last populated the store.
       *
       * Uses its own `virtualAccountLoading` flag instead of the
       * shared `isLoading`, since this is routinely called alongside
       * getWallet/getTransactions/etc. in a single Promise.all — sharing
       * one flag would make each call's loading state unreliable.
       */
      getVirtualAccountDetails: async () => {
        set({ virtualAccountLoading: true, error: null });
        try {
          const response = await walletAPI.getVirtualAccountDetails();
          set({ virtualAccount: response.data?.account || null });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get virtual account details error:', message);
          set({ error: message });
          throw error;
        } finally {
          set({ virtualAccountLoading: false });
        }
      },

      /**
       * Get virtual account transactions
       */
      getVirtualAccountTransactions: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.getVirtualAccountTransactions(params);
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      /**
       * Deactivate virtual account
       */
      deactivateVirtualAccount: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.deactivateVirtualAccount();

          if (response.success) {
            set({ virtualAccount: null });
          }

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // ============================================
      // KYC METHODS
      // ============================================

      /**
       * Update KYC information (BVN/NIN)
       */
      updateKYC: async (bvn, nin) => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.updateKYC(bvn, nin);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      /**
       * Get KYC verification status
       */
      getKYCStatus: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await walletAPI.getKYCStatus();
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // ============================================
      // REAL-TIME UPDATE METHODS (no toasts)
      // ============================================

      updateBalanceRealTime: (data) => {
        set(state => ({
          wallet: {
            ...state.wallet,
            balance: data.newBalance,
            updatedAt: data.updatedAt
          }
        }));
      },

      addTransactionRealTime: (transaction) => {
        set(state => ({
          transactions: [transaction, ...state.transactions.slice(0, 49)],
          transactionSummary: state.transactionSummary ? {
            ...state.transactionSummary,
            summary: {
              ...state.transactionSummary.summary,
              totalDeposits: transaction.type === 'CREDIT'
                ? (state.transactionSummary.summary.totalDeposits || 0) + transaction.amount
                : state.transactionSummary.summary.totalDeposits,
              totalWithdrawals: transaction.type === 'DEBIT'
                ? (state.transactionSummary.summary.totalWithdrawals || 0) + transaction.amount
                : state.transactionSummary.summary.totalWithdrawals,
              depositCount: transaction.type === 'CREDIT'
                ? (state.transactionSummary.summary.depositCount || 0) + 1
                : state.transactionSummary.summary.depositCount,
              withdrawalCount: transaction.type === 'DEBIT'
                ? (state.transactionSummary.summary.withdrawalCount || 0) + 1
                : state.transactionSummary.summary.withdrawalCount,
            }
          } : state.transactionSummary
        }));
      },

      updateTransactionStatus: (transactionId, status, data = {}) => {
        set(state => ({
          transactions: state.transactions.map(tx =>
            tx.id === transactionId ? { ...tx, status, ...data } : tx
          )
        }));
      },

      handleTransactionSuccess: (transaction) => {
        const state = get();

        if (transaction.type === 'CREDIT') {
          state.updateBalanceRealTime({
            newBalance: (state.wallet?.balance || 0) + transaction.amount,
            changeAmount: transaction.amount,
            changeType: 'CREDIT'
          });
        }

        state.addTransactionRealTime(transaction);
      },

      handleTransactionFailed: (data) => {
        get().updateTransactionStatus(data.transactionId, 'FAILED', data);
      },

      // ============================================
      // UTILITY METHODS
      // ============================================

      clearError: () => set({ error: null }),

      updateBalance: (amount, type) => {
        const currentBalance = get().wallet?.balance || 0;
        let newBalance = currentBalance;

        if (type === 'CREDIT') {
          newBalance = currentBalance + amount;
        } else if (type === 'DEBIT') {
          newBalance = currentBalance - amount;
        }

        set((state) => ({
          wallet: {
            ...state.wallet,
            balance: newBalance,
          },
        }));
      },

      setUser: (user) => {
        set({ user });
      },
    }),
    {
      name: 'wallet-storage',
      partialize: (state) => ({
        wallet: state.wallet,
        transactionSummary: state.transactionSummary,
        virtualAccount: state.virtualAccount,
        pendingTransfers: state.pendingTransfers,
        user: state.user,
      }),
    }
  )
);

export default useWalletStore;